import React from 'react';
import { 
  Box,
  Container,
  Grid,
  Typography,
  Link as MuiLink,
  List,
  ListItem,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useContent } from '../context/ContentContext';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import FaxIcon from '@mui/icons-material/Fax';

// Styled components - Heron Power inspired minimalist design
const FooterWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  color: '#000',
  marginTop: 'auto',
  paddingTop: theme.spacing(10),
  paddingBottom: theme.spacing(6),
  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  marginBottom: theme.spacing(3),
  fontSize: '14px',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: '#666',
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: '#000',
  textDecoration: 'none',
  display: 'block',
  marginBottom: theme.spacing(1.5),
  fontSize: '14px',
  transition: 'opacity 0.2s ease',
  '&:hover': {
    opacity: 0.6,
  },
}));

const SocialIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  border: '1px solid rgba(0, 0, 0, 0.1)',
  marginRight: theme.spacing(1),
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#000',
    borderColor: '#000',
    color: '#fff',
  },
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1.5),
}));

const ContactIcon = styled(Box)(({ theme }) => ({
  color: '#666',
  marginRight: theme.spacing(1.5),
  marginTop: 2,
}));

function Footer() {
  const { content } = useContent();
  const { site, footer } = content;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  
  const getSocialIcon = (network) => {
    switch (network) {
      case 'facebook':
        return <FacebookIcon fontSize="small" />;
      case 'twitter':
        return <TwitterIcon fontSize="small" />;
      case 'instagram':
        return <InstagramIcon fontSize="small" />;
      case 'linkedin':
        return <LinkedInIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // Translate column titles
  const getTranslatedTitle = (title) => {
    switch(title) {
      case 'About Us':
        return t('footer.aboutUs');
      case 'Quick Links':
        return t('footer.quickLinks');
      case 'Services':
        return t('footer.services');
      case 'Contact':
        return t('footer.contact');
      default:
        return title;
    }
  };

  // Translate link texts
  const getTranslatedLink = (text) => {
    switch(text) {
      case 'Home':
        return t('navigation.home');
      case 'About':
        return t('navigation.about');
      case 'Services':
        return t('navigation.services');
      case 'Projects':
        return t('navigation.projects');
      case 'Blog':
        return t('navigation.blog');
      case 'Contact':
        return t('navigation.contact');
      case 'Excavation':
        return t('home.services.excavation');
      case 'Grading':
        return t('home.services.grading');
      case 'Demolition':
        return t('home.services.demolition');
      case 'Land Clearing':
        return t('home.services.landClearing');
      default:
        return text;
    }
  };

  // Social networks that are disabled (no account yet)
  const disabledSocials = ['facebook', 'instagram', 'twitter'];

  return (
    <FooterWrapper>
      <Container maxWidth="lg">
        <Grid container spacing={6}>
          {footer.columns.map((column, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <FooterTitle variant="h6">
                {getTranslatedTitle(column.title)}
              </FooterTitle>

              {column.content && (
                <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7, fontSize: '14px' }}>
                  {i18n.language === 'zh' && column.content_zh ? column.content_zh : column.content}
                </Typography>
              )}

              {column.links && (
                <Box>
                  {column.links.map((link, linkIndex) => (
                    <FooterLink to={link.path} key={linkIndex}>
                      {getTranslatedLink(link.text)}
                    </FooterLink>
                  ))}
                </Box>
              )}

              {column.contact && (
                <Box>
                  <ContactItem>
                    <ContactIcon>
                      <LocationOnIcon sx={{ fontSize: 18 }} />
                    </ContactIcon>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                      {i18n.language === 'zh' && site.address_zh ? site.address_zh : site.address}
                    </Typography>
                  </ContactItem>


                  {site.cell && (
                    <ContactItem>
                      <ContactIcon>
                        <SmartphoneIcon sx={{ fontSize: 18 }} />
                      </ContactIcon>
                      <MuiLink
                        href={`tel:${site.cell}`}
                        sx={{
                          color: '#000',
                          textDecoration: 'none',
                          fontSize: '14px',
                          '&:hover': { opacity: 0.6 }
                        }}
                      >
                        {site.cell}
                      </MuiLink>
                    </ContactItem>
                  )}

                  <ContactItem>
                    <ContactIcon>
                      <EmailIcon sx={{ fontSize: 18 }} />
                    </ContactIcon>
                    <MuiLink
                      href={`mailto:${site.email}`}
                      sx={{
                        color: '#000',
                        textDecoration: 'none',
                        fontSize: '14px',
                        '&:hover': { opacity: 0.6 }
                      }}
                    >
                      {site.email}
                    </MuiLink>
                  </ContactItem>
                </Box>
              )}

              {index === 0 && (
                <Box sx={{ display: 'flex', mt: 4 }}>
                  {Object.entries(site.social).map(([network, url]) => {
                    const isDisabled = disabledSocials.includes(network);
                    if (isDisabled) {
                      return (
                        <SocialIcon
                          key={network}
                          sx={{
                            color: '#ccc',
                            borderColor: 'rgba(0, 0, 0, 0.05)',
                            cursor: 'default',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              borderColor: 'rgba(0, 0, 0, 0.05)',
                              color: '#ccc',
                            },
                          }}
                        >
                          {getSocialIcon(network)}
                        </SocialIcon>
                      );
                    }
                    return (
                      <MuiLink
                        key={network}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        <SocialIcon>
                          {getSocialIcon(network)}
                        </SocialIcon>
                      </MuiLink>
                    );
                  })}
                </Box>
              )}
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 6, borderColor: 'rgba(0, 0, 0, 0.08)' }} />

        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'center' : 'flex-start',
          textAlign: isMobile ? 'center' : 'left',
          gap: 2
        }}>
          <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
            © {new Date().getFullYear()} {i18n.language === 'zh' ? '道择' : 'Dozer'}. {i18n.language === 'zh' ? '保留所有权利。' : 'All rights reserved.'}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 3,
              flexWrap: 'wrap',
              justifyContent: isMobile ? 'center' : 'flex-end'
            }}
          >
            <MuiLink
              href="/privacy-policy"
              sx={{
                color: '#666',
                textDecoration: 'none',
                fontSize: '12px',
                '&:hover': { color: '#000' }
              }}
            >
              {i18n.language === 'zh' ? "隐私政策" : "Privacy Policy"}
            </MuiLink>
            <MuiLink
              href="/terms-of-service"
              sx={{
                color: '#666',
                textDecoration: 'none',
                fontSize: '12px',
                '&:hover': { color: '#000' }
              }}
            >
              {i18n.language === 'zh' ? "服务条款" : "Terms of Service"}
            </MuiLink>
          </Box>
        </Box>
      </Container>
    </FooterWrapper>
  );
}

export default Footer; 